import { SurveyHttpError } from "./transport";
import type { Question } from "../util/types";
import type { SurveyCommand, SurveyTransport } from "./transport";
import type { SurveyCopy } from "../theme/copy";
import { defaultThemeV2 } from "../theme/defaultThemeV2";

export type SurveyRetryPolicy = Readonly<{
  max5xxRetries: number;
  max4xxRetries: number;
  maxNetworkRetries: number;
  maxRecoveryRetries: number;
  commandWindowMs: number;
  overallDeadlineMs: number;
  requestTimeoutMs: number;
  recoveryTimeoutMs: number;
  backoffBaseMs: number;
}>;

export const DEFAULT_SURVEY_RETRY_POLICY: SurveyRetryPolicy = {
  max5xxRetries: 2,
  max4xxRetries: 1,
  maxNetworkRetries: 2,
  maxRecoveryRetries: 1,
  commandWindowMs: 22_000,
  overallDeadlineMs: 30_000,
  requestTimeoutMs: 6_000,
  recoveryTimeoutMs: 4_000,
  backoffBaseMs: 250,
};

class RequestTimeoutError extends Error {}

const requestOnce = async (
  transport: SurveyTransport,
  command: SurveyCommand,
  signal: AbortSignal,
  timeoutMs: number,
  errors: SurveyCopy["errors"],
): Promise<Question | null> => {
  if (signal.aborted) throw new Error(errors.requestCancelled);
  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout>;
  let abortRequest!: () => void;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      controller.abort();
      reject(new RequestTimeoutError(errors.requestTimeout));
    }, timeoutMs);
  });
  const cancelled = new Promise<never>((_, reject) => {
    abortRequest = () => {
      controller.abort();
      reject(new Error(errors.requestCancelled));
    };
    signal.addEventListener("abort", abortRequest, { once: true });
  });

  try {
    return await Promise.race([
      transport.request(command, controller.signal),
      timeout,
      cancelled,
    ]);
  } finally {
    clearTimeout(timeoutId!);
    signal.removeEventListener("abort", abortRequest);
    controller.abort();
  }
};

const waitForRetry = (
  delayMs: number,
  signal: AbortSignal,
  errors: SurveyCopy["errors"],
): Promise<void> =>
  new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer);
      reject(new Error(errors.requestCancelled));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, delayMs);
    signal.addEventListener("abort", onAbort, { once: true });
  });

const retryKind = (error: unknown): "5xx" | "4xx" | "network" => {
  if (error instanceof SurveyHttpError) {
    if (error.status >= 500) return "5xx";
    if (error.status >= 400 && error.status < 500) return "4xx";
  }
  return "network";
};

export const requestWithRecovery = async (
  transport: SurveyTransport,
  command: SurveyCommand,
  signal: AbortSignal,
  onRecovering: () => void,
  policy: SurveyRetryPolicy = DEFAULT_SURVEY_RETRY_POLICY,
  errors: SurveyCopy["errors"] = defaultThemeV2.copy.errors,
): Promise<Question | null> => {
  const startedAtMs = Date.now();
  const commandDeadlineMs = startedAtMs + policy.commandWindowMs;
  const overallDeadlineMs = startedAtMs + policy.overallDeadlineMs;
  const retryCounts = { "5xx": 0, "4xx": 0, network: 0 };
  const retryLimits = {
    "5xx": policy.max5xxRetries,
    "4xx": policy.max4xxRetries,
    network: policy.maxNetworkRetries,
  };
  let totalRetries = 0;
  let lastError: unknown = new Error(errors.requestDeadline);

  while (Date.now() < commandDeadlineMs && !signal.aborted) {
    const timeoutMs = Math.min(
      policy.requestTimeoutMs,
      commandDeadlineMs - Date.now(),
    );
    try {
      return await requestOnce(transport, command, signal, timeoutMs, errors);
    } catch (error) {
      if (signal.aborted) throw error;
      lastError = error;
      const kind = retryKind(error);
      if (retryCounts[kind] >= retryLimits[kind]) break;
      retryCounts[kind] += 1;
      totalRetries += 1;
      const delayMs = Math.min(
        policy.backoffBaseMs * 2 ** (totalRetries - 1),
        policy.requestTimeoutMs,
      );
      if (Date.now() + delayMs >= commandDeadlineMs) break;
      await waitForRetry(delayMs, signal, errors);
    }
  }

  if (signal.aborted) throw new Error(errors.requestCancelled);
  onRecovering();
  for (let attempt = 0; attempt <= policy.maxRecoveryRetries; attempt += 1) {
    const remainingMs = overallDeadlineMs - Date.now();
    if (remainingMs <= 0) break;
    try {
      return await requestOnce(
        transport,
        { kind: "load" },
        signal,
        Math.min(policy.recoveryTimeoutMs, remainingMs),
        errors,
      );
    } catch (error) {
      if (signal.aborted) throw error;
      lastError = error;
    }
  }
  throw lastError;
};
